import AddIcon from '@mui/icons-material/Add';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SortIcon from '@mui/icons-material/Sort';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { red } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import useToggle from '../../../../hooks/useToggle';
import useWindowDimensions from '../../../../hooks/useWindowDimensions';
import UserContext from '../../../../shared-components/contexts/UserContext';
import NoItemBox from '../../../../shared-components/NoItemBox';
import ExpandedNotation from '../../../../shared-components/Notation/ExpandedNotation';
import { INotation } from '../../../../shared-interfaces/Notation/INotation';
import CookieService from '../../../../shared-services/CookieService';
import NotationService from '../../../../shared-services/Notation/NotationService';
import { AlertStatus } from '../../../../shared-types/Notation/AlertStatus';
import { NotationType } from '../../../../shared-types/Notation/NotationType';
import INotationFilters, {
  emptyNotationFilters,
} from '../../Interfaces/INotationFilters';
import MetricFooter, { MetricFooterSkeleton } from '../MetricFooter';
import RadioImpersonatorIcon from '../RadioImpersonatorIcon';
import CreateNotationDialog from './CreateNotationDialog';
import NotationFilterPopoverContent from './NotationFilterPopoverContent';
import NotationListItem, { NotationListItemSkeleton } from './NotationListItem';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

const useStyles = makeStyles(theme => ({
  headerToolbar: {
    marginLeft: 0,
    padding: '0 10px',
    width: '100%',
    backgroundColor: '#f9f9f9',
    minHeight: '52px',
  },
  toolbar: {
    position: 'sticky',
    top: '0px',
    zIndex: 100,
    backgroundColor: 'white',
    height: '48px',
    minHeight: '48px',
    paddingLeft: 0,
    paddingRight: 0,
    marginLeft: 0,
    marginRight: 0,
    borderBottom: '1px solid #dcdcdc',
    borderTop: '1px solid #dcdcdc',
  },
  footer: {
    paddingRight: '5px',
    minHeight: '25px',
    borderTop: '1px solid #dcdcdc',
  },
  toolbarButton: {
    margin: '0 3px',
  },
  menuItem: {
    height: '40px',
  },
  header: {
    fontSize: '1.25rem',
    fontWeight: 500,
    margin: '0',
  },
}));

interface INotations {
  showInNetworkOnly: boolean | undefined;
}

interface ISeenNotations {
  id: number;
  seenDate: Date;
}

const Notations = (props: INotations) => {
  const PAGE_SIZE = 25;
  const SEEN_NOTATION_COOKIE_NAME = 'home_seenNotations';

  const classes = useStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('md'));

  const user = useContext(UserContext);
  const { height } = useWindowDimensions();
  const { enqueueSnackbar } = useSnackbar();

  const [initialLoad, setInitialLoad] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [openCreateNotationDialog, setOpenCreateNotationDialog] =
    useState(false);

  const [totalOpenAlerts, setTotalOpenAlerts] = useState<number>();
  const [openFilter, toggleOpenFilter] = useToggle(false);

  const [notationFilters, setNotationFilters] =
    useState<INotationFilters>(emptyNotationFilters);

  const [seenNotations, setSeenNotations] = useState<Array<ISeenNotations>>([]);

  const [newestToOldest, setNewestToOldest] = useState<boolean>(true);

  const [currentIndex, setCurrentIndex] = useState<number>(1);
  const [items, setItems] = useState<INotation[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isExhausted, setIsExhausted] = useState(false);

  const [addResponseTo, setAddResponseTo] = useState<INotation | undefined>();

  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<
    (EventTarget & HTMLButtonElement) | null
  >(null);
  const openSortMenu = Boolean(sortMenuAnchorEl);

  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState<
    (EventTarget & HTMLButtonElement) | null
  >(null);
  const openFilterMenu = Boolean(filterMenuAnchorEl);

  useEffect(() => {
    const seenAlertString = CookieService.Get(SEEN_NOTATION_COOKIE_NAME);
    const expires = new Date();
    expires.setHours(0, 0, 0, 0);
    expires.setDate(expires.getDate() + 1);

    if (!seenAlertString || seenAlertString === 'undefined') {
      CookieService.Create(
        SEEN_NOTATION_COOKIE_NAME,
        JSON.stringify(seenNotations),
        undefined,
        undefined,
        'lax',
        undefined,
        expires.toLocaleDateString(),
      );
    } else {
      setSeenNotations(JSON.parse(seenAlertString) as Array<ISeenNotations>);
    }
  }, []);

  useEffect(() => {
    if (props.showInNetworkOnly !== undefined) {
      setTotalOpenAlerts(undefined);
      getTotalOpenAlerts();
    }
  }, [props.showInNetworkOnly]);

  useEffect(() => {
    resetPageState();
  }, [props.showInNetworkOnly, newestToOldest, notationFilters]);

  useEffect(() => {
    if (
      props.showInNetworkOnly !== undefined &&
      !isExhausted &&
      items.length === 0 &&
      !initialLoad
    ) {
      setInitialLoad(true);

      getter()
        .then(r => {
          setItems([...items, ...r]);
        })
        .catch(e => {
          enqueueSnackbar(
            'Failed to load Notations. Please refresh this page and try again later',
            {
              variant: 'error',
            },
          );
        })
        .finally(() => {
          setInitialLoad(false);
        });
    }
  }, [isExhausted, items, notationFilters]);

  const handleSeenNotationCookie = (notationId: number) => {
    const expires = new Date();
    expires.setHours(0, 0, 0, 0);
    expires.setDate(expires.getDate() + 1);

    CookieService.Create(
      SEEN_NOTATION_COOKIE_NAME,
      JSON.stringify(getObjectFromSeenNotations(notationId)),
      undefined,
      undefined,
      'lax',
      undefined,
      expires.toLocaleDateString(),
    );

    setSeenNotations(s => {
      if (s.findIndex(n => n.id === notationId) > -1) {
        const newArrayOfStuff = seenNotations.filter(n => n.id !== notationId);
        return newArrayOfStuff.concat([
          { id: notationId, seenDate: new Date() },
        ]);
      } else {
        return s.concat([{ id: notationId, seenDate: new Date() }]);
      }
    });
  };

  function resetPageState() {
    if (props.showInNetworkOnly !== undefined) {
      setCurrentIndex(1);
      setItems([]);
      setIsExhausted(false);
    }
  }

  function getObjectFromSeenNotations(notationId: number) {
    if (seenNotations.findIndex(n => n.id === notationId) > -1) {
      const newArrayOfStuff = seenNotations.filter(n => n.id !== notationId);
      return newArrayOfStuff.concat([{ id: notationId, seenDate: new Date() }]);
    } else {
      return seenNotations.concat([{ id: notationId, seenDate: new Date() }]);
    }
  }

  const getter = useCallback(async () => {
    return NotationService.search({
      pageSize: PAGE_SIZE,
      page: currentIndex,
      searchParameters: {
        alertStatus: notationFilters.alertStatus,
        notationTypes: notationFilters.notationTypes,
        categoryIds: notationFilters.category
          ? [notationFilters.category.id]
          : undefined,
        fromDate: notationFilters.fromDate
          ? notationFilters.fromDate.toDateString()
          : dayjs().startOf('M').subtract(6, 'M').toString(),
        toDate: notationFilters.toDate
          ? dayjs.utc(notationFilters.toDate).endOf('day').toString()
          : undefined,
        createdById: notationFilters.createdBy?.id,
        studentId: notationFilters.studentId?.id,
        assignedToId: notationFilters.assignedTo?.id,
        onlyUnassigned: notationFilters.onlyUnassigned,
        onlyMyStudents: props.showInNetworkOnly,
        orderByDescending: newestToOldest,
      },
    }).then(result => {
      setCurrentIndex(result.page + 1);
      setTotalItems(result.totalItems);
      if (result.page >= result.totalPages) {
        setIsExhausted(true);
      }
      return result.items.filter(x => x.student !== null); // something wonky with the returned values
    });
  }, [notationFilters, newestToOldest, currentIndex, props.showInNetworkOnly]);

  const getTotalOpenAlerts = useCallback(async () => {
    return NotationService.search({
      pageSize: 0,
      searchParameters: {
        alertStatus: [AlertStatus.Open],
        notationTypes: [NotationType.Alert],
        onlyUnassigned: false,
        onlyMyStudents: props.showInNetworkOnly,
      },
    }).then(result => {
      setTotalOpenAlerts(result.totalItems);
    });
  }, [props.showInNetworkOnly]);

  const updateTotalOpenAlerts = (n: number) => {
    if (totalOpenAlerts) {
      setTotalOpenAlerts(o => {
        if (o) {
          if (isNaN(o + n)) {
            getter();
            return undefined;
          }
          return o + n;
        } else {
          return undefined;
        }
      });
    }
  };

  const handleOpenFilter = (isOpen: boolean) => {
    if (!openFilter) {
      setNotationFilters({
        notationTypes: [NotationType.Alert],
        alertStatus: [AlertStatus.Open],
        fromDate: dayjs().startOf('M').subtract(6, 'M').toDate(),
        category: undefined,
        toDate: undefined,
        createdBy: undefined,
        studentId: undefined,
        assignedTo: undefined,
        onlyUnassigned: false,
      });
    } else {
      setNotationFilters(emptyNotationFilters);
    }
    toggleOpenFilter();
  };
  return (
    <Paper elevation={2}>
      <Toolbar variant="regular" className={classes.headerToolbar}>
        <div style={{ width: '100%' }}>
          <Box display="flex">
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h2" className={classes.header}>
                Notations
              </Typography>
            </Box>
            <Box justifyContent="flex-end">
              {totalOpenAlerts >= 0 ? (
                <Button
                  style={
                    openFilter
                      ? {
                          border: `1px solid ${red[700]}`,
                          backgroundColor: red[700],
                          color: '#fff',
                          fontWeight: 600,
                          textTransform: 'none',
                        }
                      : {
                          border: `1px solid ${red[700]}`,
                          backgroundColor: '#fff',
                          color: red[700],
                          fontWeight: 600,
                          textTransform: 'none',
                        }
                  }
                  size="small"
                  variant={openFilter ? 'contained' : 'text'}
                  onClick={() => handleOpenFilter(openFilter)}
                  endIcon={openFilter ? <HighlightOffIcon /> : undefined}
                >{`${totalOpenAlerts.toLocaleString()} Open Alerts`}</Button>
              ) : (
                <Skeleton variant="text" height="52px" width="140px" />
              )}
            </Box>
          </Box>
        </div>
      </Toolbar>

      <Toolbar className={classes.toolbar}>
        <Box
          display="flex"
          style={{ width: '100%', paddingLeft: 0, paddingRight: '4px' }}
        >
          <Box p={0} flexGrow={1}>
            <Button
              aria-label="Create Notation"
              size="small"
              className={classes.toolbarButton}
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateNotationDialog(true)}
              style={{ marginLeft: '10px' }}
            >
              Create Notation
            </Button>
          </Box>
          <Box>
            <Button
              aria-label="Alert Menu"
              size={'small'}
              className={classes.toolbarButton}
              aria-describedby={openSortMenu ? 'alerts-sort' : undefined}
              startIcon={!isXs && <SortIcon />}
              color="primary"
              variant="outlined"
              onClick={e => {
                setSortMenuAnchorEl(e.currentTarget);
              }}
            >
              {isXs ? <SortIcon /> : 'Sort'}
            </Button>
            <Menu
              id={openSortMenu ? 'alerts-sort' : undefined}
              open={openSortMenu}
              anchorEl={sortMenuAnchorEl}
              onClose={() => {
                setSortMenuAnchorEl(null);
              }}
              style={{ paddingRight: '10px' }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              keepMounted
            >
              <MenuItem
                onClick={() => {
                  setNewestToOldest(true);
                  setSortMenuAnchorEl(null);
                }}
                className={classes.menuItem}
              >
                <RadioImpersonatorIcon checked={newestToOldest} />
                <Typography>Newest to Oldest</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setNewestToOldest(false);
                  setSortMenuAnchorEl(null);
                }}
                className={classes.menuItem}
              >
                <RadioImpersonatorIcon checked={!newestToOldest} />
                <Typography>Oldest to Newest</Typography>
              </MenuItem>
            </Menu>
            <Button
              aria-label="Filter Menu"
              size={'small'}
              className={classes.toolbarButton}
              aria-describedby={openFilterMenu ? 'Filters' : undefined}
              startIcon={!isXs && <TuneIcon />}
              color="primary"
              variant="outlined"
              onClick={e => {
                setFilterMenuAnchorEl(e.currentTarget);
              }}
            >
              {isXs ? <TuneIcon /> : 'Filters'}
            </Button>
            <Popover
              id={openFilterMenu ? 'Filters' : undefined}
              open={openFilterMenu}
              onClose={() => setFilterMenuAnchorEl(null)}
              anchorEl={filterMenuAnchorEl}
              PaperProps={{
                style: { padding: '15px', width: '480px' },
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
            >
              <NotationFilterPopoverContent
                open={openFilterMenu}
                close={() => setFilterMenuAnchorEl(null)}
                notationFilters={notationFilters}
                onApply={f => {
                  toggleOpenFilter(false);
                  setNotationFilters(f);
                }}
              />
            </Popover>
          </Box>
        </Box>
      </Toolbar>

      {items.length === 0 && isExhausted ? (
        <NoItemBox
          height={height - 450}
          minHeight={575}
          text="No Notations To Display"
        />
      ) : (
        <div
          style={{
            height: height - 450,
            minHeight: 575,
            overflowY: 'scroll',
            overflowX: 'hidden',
          }}
        >
          {initialLoad ? (
            <ul
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                position: 'relative',
              }}
            >
              {[...Array(PAGE_SIZE)].map((e, i) => {
                return <NotationListItemSkeleton key={i} />;
              })}
            </ul>
          ) : (
            <InfiniteScroll
              element="ul"
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                position: 'relative',
              }}
              useWindow={false}
              initialLoad={false}
              hasMore={!isExhausted && !isLoading}
              threshold={500}
              loadMore={() => {
                setIsLoading(true);
                getter()
                  .then(r => setItems([...items, ...r]))
                  .catch(e => {
                    /*fillthisin later */
                  })
                  .finally(() => {
                    setIsLoading(false);
                  });
              }}
            >
              {items.map((e, i) => {
                return (
                  <NotationListItem
                    key={e.id}
                    setAddResponseTo={setAddResponseTo}
                    onUpdate={updateTotalOpenAlerts}
                    notationSearchResponse={e}
                    hasBeenSeen={
                      seenNotations.findIndex(a => a.id === e.id) > -1 &&
                      dayjs(user.previouslyLoggedIn).isBefore(
                        dayjs(seenNotations.find(a => a.id === e.id)?.seenDate),
                      )
                    }
                    setHasBeenSeen={() => {
                      handleSeenNotationCookie(e.id);
                    }}
                  />
                );
              })}
            </InfiniteScroll>
          )}
          {isLoading && <LinearProgress aria-busy={true} />}
        </div>
      )}
      {initialLoad ? (
        <MetricFooterSkeleton />
      ) : (
        <MetricFooter currentCount={items.length} totalCount={totalItems} />
      )}
      {addResponseTo && (
        <ExpandedNotation
          notation={addResponseTo}
          close={() => setAddResponseTo(undefined)}
          open={addResponseTo !== undefined}
        />
      )}
      <CreateNotationDialog
        open={openCreateNotationDialog}
        close={() => setOpenCreateNotationDialog(false)}
        onComplete={n => {
          if (n.length === 0) {
            setTimeout(() => resetPageState(), 1000);
          } else {
            setItems([...n, ...items]);
            if (n[0].notationType === NotationType.Alert) {
              updateTotalOpenAlerts(n.length);
            }
          }
        }}
      />
    </Paper>
  );
};

export default Notations;
